import React, { useState, useCallback } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ToolCardShell } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";

interface QuestionOption {
  label?: string;
  description?: string;
}

interface QuestionData {
  question?: string;
  header?: string;
  multiple?: boolean;
  options?: QuestionOption[];
}

function extractQuestions(args: Record<string, unknown>): QuestionData[] {
  const q = args.questions || args.question;
  if (Array.isArray(q)) return q;
  if (typeof q === "object" && q !== null) return [q as QuestionData];
  return [];
}

function extractAnswers(result: unknown): Array<{ indices?: number[]; text?: string } | null> {
  if (!result) return [];
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.answers)) return r.answers;
    if (Array.isArray(r.answer)) return r.answer;
    if (r.answer !== undefined) return [r.answer as { indices?: number[]; text?: string }];
  }
  return [];
}

const OptionButton: React.FC<{
  option: QuestionOption;
  selected: boolean;
  disabled: boolean;
  isRecommended: boolean;
  onClick: () => void;
}> = ({ option, selected, disabled, isRecommended, onClick }) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-md border px-3 py-2 transition-colors",
        disabled && "cursor-default opacity-80",
        !disabled && "cursor-pointer",
        selected
          ? "border-primary bg-primary/10 text-primary"
          : isRecommended
            ? "border-primary/30 bg-primary/5 hover:bg-primary/10"
            : "border-border hover:bg-muted/50"
      )}
    >
      <span className="text-sm font-medium">{option.label}</span>
      {option.description && (
        <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
      )}
    </button>
  );
};

const QuestionBlock: React.FC<{
  questionData: QuestionData;
  questionIndex: number;
  disabled: boolean;
  existingAnswer: { indices?: number[]; text?: string } | null | undefined;
  onAnswer: (questionIndex: number, optionIndices: number[], customText?: string) => void;
}> = ({ questionData, questionIndex, disabled, existingAnswer, onAnswer }) => {
  const { question, header, multiple, options = [] } = questionData;
  const existingIndices = existingAnswer?.indices || [];
  const hasExistingAnswer = existingAnswer != null;

  const [selectedIndices, setSelectedIndices] = useState<number[]>(existingIndices);
  const [customText, setCustomText] = useState(existingAnswer?.text || "");
  const [showCustomInput, setShowCustomInput] = useState(false);

  const isDisabled = disabled || hasExistingAnswer;

  const handleOptionClick = useCallback(
    (optIndex: number) => {
      if (isDisabled) return;

      let newIndices: number[];
      if (multiple) {
        newIndices = selectedIndices.includes(optIndex)
          ? selectedIndices.filter((i) => i !== optIndex)
          : [...selectedIndices, optIndex];
      } else {
        newIndices = [optIndex];
      }
      setSelectedIndices(newIndices);
      setShowCustomInput(false);
      onAnswer(questionIndex, newIndices);
    },
    [isDisabled, multiple, selectedIndices, questionIndex, onAnswer]
  );

  const handleCustomSubmit = useCallback(() => {
    if (!customText.trim() || isDisabled) return;
    setSelectedIndices([]);
    onAnswer(questionIndex, [], customText.trim());
  }, [customText, isDisabled, questionIndex, onAnswer]);

  const handleCustomKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleCustomSubmit();
      }
    },
    [handleCustomSubmit]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        {header && (
          <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700 flex-shrink-0">
            {header}
          </span>
        )}
        {question && (
          <p className="text-sm text-foreground">{question}</p>
        )}
      </div>
      {options.length > 0 && (
        <div className="space-y-1.5">
          {options.map((opt, optIndex) => (
            <OptionButton
              key={optIndex}
              option={opt}
              selected={selectedIndices.includes(optIndex)}
              disabled={isDisabled}
              isRecommended={!!opt.label?.endsWith("(推荐)")}
              onClick={() => handleOptionClick(optIndex)}
            />
          ))}
        </div>
      )}
      {!hasExistingAnswer && (
        <div className="space-y-1.5">
          {showCustomInput ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={handleCustomKeyDown}
                placeholder="输入自定义答案..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customText.trim()}
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                提交
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              自定义输入...
            </button>
          )}
        </div>
      )}
      {hasExistingAnswer && existingAnswer?.text && (
        <div className="text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-1.5">
          {existingAnswer.text}
        </div>
      )}
    </div>
  );
};

export const InteractionCard: React.FC<ToolCardComponentProps> = ({
  toolCall,
  onAskUserAnswer,
}) => {
  const args = toolCall.args || {};
  const result = toolCall.result;
  const hasResult = result !== undefined && result !== null && !toolCall.error;

  const questions = extractQuestions(args);
  const answers = extractAnswers(result);

  const isWaiting = !hasResult && toolCall.status === "executing";

  return (
    <ToolCardShell
      toolName="向您提问"
      status={hasResult ? "success" : "executing"}
      icon={<MessageCircleQuestion className="h-4 w-4" />}
      iconColor="text-indigo-500"
      defaultExpanded={true}
    >
      <div className="px-4 py-2 flex items-center gap-2 text-xs">
        {isWaiting && (
          <span className="text-primary">等待回答</span>
        )}
        {hasResult && (
          <span className="text-success">已回答</span>
        )}
      </div>
      <div className="px-4 py-2 space-y-4">
        {questions.map((q, qi) => (
          <QuestionBlock
            key={qi}
            questionData={q}
            questionIndex={qi}
            disabled={!onAskUserAnswer}
            existingAnswer={answers[qi]}
            onAnswer={onAskUserAnswer || (() => {})}
          />
        ))}
      </div>
      {toolCall.error && (
        <div className="px-4 py-2 text-xs text-destructive">{toolCall.error}</div>
      )}
    </ToolCardShell>
  );
};
